/**
 * Automatic enquiries from incoming mail (Akhil, 2026-09-18: "for each new mail that's coming,
 * let's create an automatic enquiry").
 *
 * ---- The two rules that keep this safe ----
 *
 * 1. Only mail that ARRIVES while the page is open. The inbox holds months of RFQ-looking mail
 *    with no enquiry behind it — some handled elsewhere, some not enquiries at all — and
 *    creating a document for each on first load would flood the board. `seen` is the ids on
 *    screen when the page opened; only what appears after that is a candidate, once.
 *
 * 2. A purchaser from the mail and at least one line (2026-09-21 — the server dropped its other
 *    mandatory fields). The purchaser is the Cc convention: the responsible buyer is copied on
 *    every enquiry. The type is classified from the items; customer, location, vessel and port
 *    are pre-filled only where the mail settles them — a guessed customer is a wrong record.
 *
 * Every rule here is the create bar's own (`CreateEnquiryBar.tsx`) applied without the click.
 */

import { classifyRfqMail, isOwnOutgoingRfq, isSupplierReply, RFQ_THRESHOLD } from './mailClassify'
import { filterOutOwnCompanies } from './ownCompanies'
import { pickDeliveryLocation } from './deliveryLocation'
import { htmlToText, isInboundFolder } from './mail'
import { pickPort, pickVessel } from './vesselPort'
import { toCreateLine } from '../features/mail/createEnquiryLines'
import type { ExtractionResult } from './api/mailExtractionState'
import type { NewEnquiryHeader } from './api/useEnquiryMutations'
import type { MailMessage } from './types'

export function autoEnquiryCandidates(
  mails: readonly MailMessage[],
  state: {
    seen: ReadonlySet<string>
    processed: ReadonlyMap<string, unknown[]>
    attempted: ReadonlySet<string>
    /**
     * `"YYYY-MM-DD HH:mm:ss"` site time the page opened. A mail must have ARRIVED after it as
     * well as be unseen: the list is fetched at `limit: 1` while another folder is showing, so
     * returning to the inbox makes hundreds of old ids "unseen" at once. The timestamp is what
     * keeps the backlog out either way.
     */
    since?: string
  },
): MailMessage[] {
  return mails.filter((mail) => {
    if (state.seen.has(mail.id) || state.attempted.has(mail.id)) return false
    if (state.since && mail.received_at && mail.received_at < state.since) return false
    if ((state.processed.get(mail.id)?.length ?? 0) > 0) return false
    if (!isInboundFolder(mail.folder)) return false
    if (isOwnOutgoingRfq(mail.subject) || isSupplierReply(mail)) return false
    return classifyRfqMail(mail).confidence >= RFQ_THRESHOLD
  })
}

export interface AutoEnquiryContext {
  ownCompanies: readonly string[]
  locations: readonly string[]
  /** The session's operating company — optional since 2026-09-21; the header card asks otherwise. */
  company: string
  priceLists: ReadonlyMap<string, string>
  /** Every user with the purchaser role — the mail's Cc is matched against these. */
  purchasers: readonly string[]
  vessels: readonly { code: string; name: string }[]
  ports: readonly string[]
  /** Decided from the items by `classifyEnquiryType`; `null` leaves the type for a person. */
  enquiryType: string | null
}

export type AutoEnquiryPlan = { ok: true; header: NewEnquiryHeader } | { ok: false; reason: string }

export const AUTO_ENQUIRY_MARK = 'Created automatically by Bosun from mail'

/** The purchaser on the mail: first Cc that is a purchaser, else first To. */
export function purchaserOnMail(mail: Pick<MailMessage, 'to' | 'cc'>, purchasers: readonly string[]): string | null {
  const known = new Map(purchasers.map((p) => [p.trim().toLowerCase(), p]))
  for (const addr of [...mail.cc, ...mail.to]) {
    const hit = known.get(addr.trim().toLowerCase())
    if (hit) return hit
  }
  return null
}

/*
  The rules since 2026-09-21 (`create_enquiry` no longer requires customer, type or location —
  measured: only `status` and `transaction_date` are mandatory): a purchaser from the mail and at
  least one line are what an automatic enquiry needs. Everything else is pre-filled where the mail
  resolves it and left BLANK where it does not — the RFQ builder's header card names the gaps
  before an RFQ can go. A blank is honest; a guessed customer would be a wrong record.
*/
export function planAutoEnquiry(mail: MailMessage, result: ExtractionResult, ctx: AutoEnquiryContext): AutoEnquiryPlan {
  // Lines with no readable quantity go through at 0 (2026-09-22) — the purchaser completes them.
  const lines = result.items.map(toCreateLine).filter((l) => l.qty >= 0)
  if (lines.length === 0) return { ok: false, reason: 'no item lines could be read' }

  const purchaser = purchaserOnMail(mail, ctx.purchasers)
  if (!purchaser) return { ok: false, reason: 'no purchaser on the mail’s Cc or To' }

  const text = `${mail.subject}\n${htmlToText(mail.body_html ?? '')}`
  const customers = filterOutOwnCompanies(result.customer_matches ?? [], [...ctx.ownCompanies])
  const location = pickDeliveryLocation(text, [...ctx.locations])
  const vessel = pickVessel(text, ctx.vessels)
  const port = pickPort(text, ctx.ports)

  return {
    ok: true,
    header: {
      source: 'mail',
      mail_id: mail.id,
      purchaser,
      ...(customers.length === 1 ? { customer: customers[0] } : {}),
      ...(ctx.company.trim() ? { company: ctx.company } : {}),
      ...(ctx.enquiryType ? { enquiry_type: ctx.enquiryType } : {}),
      delivery_location: location.matched ? location.location : null,
      price_list: location.matched ? (ctx.priceLists.get(location.location) ?? null) : null,
      vessel,
      delivery_port: port,
      enquiry_remarks: `${AUTO_ENQUIRY_MARK} ${mail.id} (${mail.subject.trim().slice(0, 80)})`,
      items: lines,
    },
  }
}
